import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { ResumeIndicatorB3ByHealthTeamData } from "@/services/esus/indicator/esb/b3/getResumeIndicatorB3ByHealthTeam";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getIndicatorClassification, IndicatorEsbEnum } from "@/utils/indicatorsEsb";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { IndicatorEsbB3HealthTeamsFilters } from "./IndicatorEsbB3HealthTeamsFilters";
import { IndicatorEsbB3HealthTeamsClassificationTable } from "./IndicatorEsbB3HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsbB3HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorB3ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorB3ByHealthTeam: () => Promise<ResumeIndicatorB3ByHealthTeamData[]>;
};

export function IndicatorEsbB3HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorB3ByHealthTeam,
}: Readonly<IndicatorEsbB3HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorB3ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorB3AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Procedimentos por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorB3AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorB3ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					"Equipe ESB": healthTeamClassification.esbHealthTeamName,
					"Equipe ESB INE": healthTeamClassification.esbHealthTeamIne,
					"Equipe ESF": healthTeamClassification.linkedHealthTeamName,
					"Equipe ESF INE": healthTeamClassification.linkedHealthTeamIne,
					"Procedimentos exodontias em cidadãos vinculados":
						healthTeamClassification.totalToothExtractionProceduresLinked ?? 0,
					"Procedimentos exodontias em cidadãos não vinculados":
						healthTeamClassification.totalToothExtractionProcedures -
						healthTeamClassification.totalToothExtractionProceduresLinked,
					"Procedimentos exodontias":
						healthTeamClassification.totalToothExtractionProcedures,
					"Procedimentos preventivos curativos e exodontias em cidadãos vinculados":
						healthTeamClassification.totalProceduresLinked ?? 0,
					"Procedimentos preventivos curativos e exodontias em cidadãos não vinculados":
						healthTeamClassification.totalProcedures -
						healthTeamClassification.totalProceduresLinked,
					"Procedimentos preventivos curativos e exodontias":
						healthTeamClassification.totalProcedures,
					"% Classificação": `${calculatePercentage(
						healthTeamClassification.totalToothExtractionProcedures,
						healthTeamClassification.totalProcedures
					)}%`,
					Classificação: getIndicatorClassification(
						IndicatorEsbEnum.B3,
						calculatePercentage(
							healthTeamClassification.totalToothExtractionProcedures,
							healthTeamClassification.totalProcedures
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsbB3HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsbB3HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
